/* oxlint-disable tailwindcss/no-unknown-classes -- Styles are colocated in home-new.css. */

import heroSaturnSrc from "@/assets/home-new/hero-trails.avif"
import type { TrailMonitorResult } from "@/data/trails/trailModel"
import { ChangeMergeStatus } from "@/routes/_app/$forge/$project/$repo/trails/$number/{-$slug}/changes/-components/ChangeMergeStatus"
import { ChangeMonitors } from "@/routes/_app/$forge/$project/$repo/trails/$number/{-$slug}/changes/-components/ChangeMonitors"
import {
  GateList,
  type GateListItem,
} from "@/routes/_app/$forge/$project/$repo/trails/$number/{-$slug}/changes/-components/gates/GateList"
import type { MonitorsSection } from "@/routes/_app/$forge/$project/$repo/trails/$number/{-$slug}/changes/-lib/types"

import { glowRingClassName, newBadgeClassName } from "./homeNewStyles"
import { TrailsIcon } from "./TrailsIcon"
import { useRevealOnce } from "./useRevealOnce"
import { useScrollToProduct } from "./useScrollToProduct"
import { glareClassName, tiltHoverClassName, tiltHoverProps } from "./useTiltHover"

// The hero shows a trail that is ready to merge, rendered with the product's own gate
// and monitor components. The panels and their rows ease in with a stagger on
// load (see `.home-new-trail-*` in home-new.css).
const gates: GateListItem[] = [
  {
    key: "approvals",
    gateType: "approvals",
    name: "Approvals",
    detail: "Approved by Daniel Adams",
    tone: "success",
  },
  {
    key: "findings",
    gateType: "findings",
    name: "Findings",
    detail: "None blocking",
    tone: "success",
  },
  { key: "checks", gateType: "checks", name: "Checks", detail: "5 of 5 passed", tone: "success" },
  { key: "deployed", gateType: "eval", name: "Deployed", detail: "5 of 5 passed", tone: "success" },
]

const historyTimes = [
  "2026-10-12T09:00:00Z",
  "2026-10-13T09:00:00Z",
  "2026-10-14T09:00:00Z",
  "2026-10-15T09:00:00Z",
  "2026-10-16T09:00:00Z",
  "2026-10-17T09:00:00Z",
]

type MonitorReading = Pick<TrailMonitorResult, "percent_value" | "size_value">

/** A monitor result with history, for static Trail mocks. */
function monitorResult(
  base: Pick<TrailMonitorResult, "key" | "label" | "value_type" | "polarity">,
  readings: MonitorReading[],
): TrailMonitorResult {
  const points = readings.map((reading, index) => ({
    percent_value: reading.percent_value,
    size_value: reading.size_value,
    boolean_value: null,
    rationale: null,
    run_id: `${base.key}-${index}`,
    head_sha: null,
    updated_at: historyTimes[index] ?? historyTimes.at(-1)!,
  }))
  const latest = points.at(-1)!

  return {
    ...base,
    state: "evaluated",
    outcome: "passed",
    percent_value: latest.percent_value,
    size_value: latest.size_value,
    boolean_value: null,
    rationale: null,
    run_id: latest.run_id,
    head_sha: null,
    updated_at: latest.updated_at,
    history: points,
  }
}

const percent = (value: number): MonitorReading => ({
  percent_value: value,
  size_value: null,
})
const size = (value: NonNullable<TrailMonitorResult["size_value"]>): MonitorReading => ({
  percent_value: null,
  size_value: value,
})

const monitors: MonitorsSection = {
  kind: "ready",
  rows: [
    monitorResult(
      { key: "risk", label: "Risk", value_type: "percent", polarity: "lower_is_better" },
      [48, 44, 22, 12, 9, 7].map(percent),
    ),
    monitorResult({ key: "slop", label: "Slop", value_type: "size", polarity: "lower_is_better" }, [
      size("large"),
      size("medium"),
      size("medium"),
      size("small"),
      size("small"),
      size("small"),
    ]),
    monitorResult(
      { key: "drift", label: "Intent drift", value_type: "percent", polarity: "lower_is_better" },
      [24, 31, 17, 26, 11, 3].map(percent),
    ),
  ],
}

// The wide tablet layout (md up to the bento breakpoint) gives the Trails card
// the full width, so it shows a fourth monitor: test coverage climbing.
const tabletMonitors: MonitorsSection = {
  kind: "ready",
  rows: [
    ...monitors.rows,
    monitorResult(
      {
        key: "coverage",
        label: "Test coverage",
        value_type: "percent",
        polarity: "higher_is_better",
      },
      [61, 64, 70, 76, 80, 84].map(percent),
    ),
  ],
}

/** The hero's featured card: a Trail's merge gates and monitors over a planet-ring vista. */
export function HomeNewTrailsCard() {
  const scrollToProduct = useScrollToProduct()

  return (
    <a
      href="#trails"
      data-product="trails"
      onClick={(event) => scrollToProduct(event, "trails")}
      className={`flex min-h-[330px] flex-col overflow-hidden rounded-lg bg-floating focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus md:col-span-2 ${tiltHoverClassName} ${glowRingClassName}`}
      {...tiltHoverProps("large")}
    >
      <div className="flex items-start gap-3 p-4">
        <TrailsIcon size={20} strokeWidth={1.5} className="mt-px shrink-0 text-default" />
        <div className="flex min-w-0 flex-col gap-0.5 leading-5">
          <p className="font-sans text-base font-medium tracking-[0.01em] text-default">Trails</p>
          <p className="font-sans text-sm text-muted">
            Move past the PR, with evals at the center of review
          </p>
        </div>
      </div>
      <span className={newBadgeClassName}>New</span>

      {/* Decorative preview: inert so the product components' buttons, tooltips
          and dialogs stay out of the tab order and never open here. It starts
          below the header, so a description that wraps on phones pushes it down
          instead of hiding under it. */}
      <div className="relative min-h-[255px] flex-1 overflow-hidden" aria-hidden="true" inert>
        {/* Ringed-planet backdrop, oversized by the parallax travel so its edges
            never show. It drifts against the pointer (see useTiltHover). */}
        <img
          src={heroSaturnSrc}
          alt=""
          width={1600}
          height={1066}
          className="home-new-parallax absolute -inset-4 size-[calc(100%+2rem)] max-w-none object-cover object-[25%_40%]"
          loading="eager"
          decoding="async"
        />

        <div className="home-new-trail-monitors md:max-bento:hidden absolute top-6 left-6 w-[680px]">
          <ChangeMonitors monitors={monitors} project="entire" repo="entire.io" />
        </div>
        {/* Wide tablets only: four monitors at the same card width. The 912px panels
            sit centred while the card has room for them and stay pinned to the
            top-left corner, clipping on the right, once it is narrower. */}
        <div className="home-new-trail-monitors md:max-bento:block md:max-bento:left-[max(1.5rem,calc(50%-456px))] absolute top-6 left-6 hidden w-[912px]">
          <ChangeMonitors monitors={tabletMonitors} project="entire" repo="entire.io" />
        </div>

        <div className="home-new-trail-gates md:max-bento:left-[max(1.5rem,calc(50%-456px))] md:max-bento:w-[912px] absolute top-[138px] left-6 w-[640px]">
          <GateList
            gates={gates}
            header={
              <ChangeMergeStatus merge={{ kind: "ready_to_merge", merge: { kind: "hidden" } }} />
            }
          />
        </div>
      </div>
      <span className={glareClassName} aria-hidden="true" />
    </a>
  )
}

/**
 * The Trails section's panel scene: the hero card's monitors and merge gates
 * at full size, centred over the section backdrop. The four monitors sit in
 * one row on wide panels and wrap to two by two on narrow ones. It mounts the
 * first time it scrolls into view, so the hero's load sequence plays then.
 */
export function TrailsShowcase() {
  const [revealed, ref] = useRevealOnce()

  return (
    <div
      ref={ref}
      className="absolute inset-0 flex justify-center px-4 pt-6 sm:items-center sm:p-12"
      inert
    >
      {revealed ? (
        <div className="home-new-trail-showcase flex w-full max-w-[760px] flex-col gap-5">
          <div className="home-new-trail-monitors">
            <ChangeMonitors monitors={tabletMonitors} project="entire" repo="entire.io" />
          </div>
          <div className="home-new-trail-gates">
            <GateList
              gates={gates}
              header={
                <ChangeMergeStatus merge={{ kind: "ready_to_merge", merge: { kind: "hidden" } }} />
              }
            />
          </div>
        </div>
      ) : null}
    </div>
  )
}
